import { useState, useEffect } from "react";
import { DealerLayout } from "@/components/shared/DealerLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { getDealerCareerApplications, updateDealerCareerApplicationInfo } from "@/lib/api";
import { toast } from "sonner";
import {
  FileText, Search, Download, Loader2, User, MapPin, Briefcase, Pencil, Save,
} from "lucide-react";

const STATUS_BADGES = {
  new: "bg-blue-100 text-blue-700",
  reviewed: "bg-yellow-100 text-yellow-700",
  interviewed: "bg-purple-100 text-purple-700",
  hired: "bg-green-100 text-green-700",
  rejected: "bg-red-100 text-red-700",
};

const POSITION_TYPE_LABELS = {
  sales_consultant: "Sales Consultant",
  sales_rep_pt: "Sales Consultant (Part-Time)",
  assistant_manager: "Assistant Manager",
  store_manager: "Store Manager",
};

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

export default function DealerApplicants() {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [editDialog, setEditDialog] = useState(false);
  const [editApp, setEditApp] = useState(null);
  const [editForm, setEditForm] = useState({ first_name: "", last_name: "", email: "", phone: "", street: "", city: "", state: "", zip_code: "" });
  const [savingEdit, setSavingEdit] = useState(false);

  const loadData = () => {
    getDealerCareerApplications()
      .then(setApplications)
      .catch(() => toast.error("Failed to load applicants"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadData(); }, []);

  const openEdit = (app) => {
    setEditApp(app);
    setEditForm({ first_name: app.first_name, last_name: app.last_name, email: app.email, phone: app.phone, street: app.street || "", city: app.city || "", state: app.state || "", zip_code: app.zip_code || "" });
    setEditDialog(true);
  };

  const saveEdit = async () => {
    if (!editApp) return;
    setSavingEdit(true);
    try {
      await updateDealerCareerApplicationInfo(editApp.id, editForm);
      toast.success("Applicant info updated");
      setEditDialog(false);
      loadData();
    } catch (e) {
      toast.error("Failed to update");
    } finally {
      setSavingEdit(false);
    }
  };

  const filtered = applications.filter((app) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      `${app.first_name} ${app.last_name}`.toLowerCase().includes(q) ||
      app.email.toLowerCase().includes(q) ||
      (app.position_title || "").toLowerCase().includes(q) ||
      (app.location_name || "").toLowerCase().includes(q)
    );
  });

  return (
    <DealerLayout title="Career Applicants" breadcrumb="Applicants">
      <div className="space-y-6" data-testid="dealer-applicants">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h2 className="font-heading font-bold text-xl text-navy">Career Applicants</h2>
            <p className="text-sm text-slate-500 font-body">{applications.length} applications for your locations</p>
          </div>
          <div className="relative max-w-xs w-full">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search applicants..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
              data-testid="dealer-applicant-search"
            />
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-teal" /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl border border-slate-200" data-testid="dealer-applicants-empty">
            <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="font-heading font-semibold text-navy text-lg mb-2">No Applicants Yet</h3>
            <p className="text-slate-500 font-body">
              {search ? "No results match your search." : "When candidates apply for positions at your locations, they'll appear here."}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map((app) => (
              <Card key={app.id} className="border-slate-200" data-testid={`dealer-app-${app.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${STATUS_BADGES[app.status] || STATUS_BADGES.new}`}>
                          {app.status}
                        </span>
                        <span className="px-2 py-0.5 bg-teal/10 text-teal text-xs font-medium rounded-full">
                          {POSITION_TYPE_LABELS[app.position_type] || app.position_type}
                        </span>
                        {app.is_nearby && (
                          <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-medium rounded-full flex items-center gap-1">
                            <MapPin className="w-3 h-3" /> Nearby (within 15 mi)
                          </span>
                        )}
                      </div>
                      <h3 className="font-heading font-semibold text-navy text-base flex items-center gap-1">
                        <User className="w-4 h-4 text-slate-400" />
                        {app.first_name} {app.last_name}
                        {app.applicant_id && <span className="text-xs font-mono bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded ml-1">{app.applicant_id}</span>}
                      </h3>
                      <div className="flex flex-wrap gap-3 mt-1 text-xs text-slate-500 font-body">
                        <span>{app.email}</span>
                        <span>{app.phone}</span>
                      </div>
                      <div className="flex items-center gap-3 mt-1.5 text-xs text-slate-400 font-body">
                        <span className="flex items-center gap-1"><Briefcase className="w-3 h-3" />{app.position_title}</span>
                        {app.location_name && (
                          <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{app.location_name} — {app.location_city}, {app.location_state}</span>
                        )}
                        <span>{new Date(app.created_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {app.resume_url && (
                        <a
                          href={`${API_URL}${app.resume_url}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 px-3 py-1.5 bg-teal/10 text-teal text-xs font-medium rounded-lg hover:bg-teal/20 transition-colors"
                          data-testid={`dealer-resume-${app.id}`}
                        >
                          <Download className="w-3.5 h-3.5" /> Resume
                        </a>
                      )}
                      <Button variant="outline" size="sm" className="h-7 text-xs gap-1" onClick={() => openEdit(app)} data-testid={`dealer-edit-${app.id}`}>
                        <Pencil className="w-3 h-3" /> Edit
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Edit Dialog */}
        <Dialog open={editDialog} onOpenChange={setEditDialog}>
          <DialogContent className="max-w-md" data-testid="dealer-edit-dialog">
            <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><Pencil className="w-5 h-5" /> Edit Applicant</DialogTitle></DialogHeader>
            <div className="space-y-4 pt-2">
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-sm text-slate-600 mb-1 block">First Name</label><Input value={editForm.first_name} onChange={(e) => setEditForm({ ...editForm, first_name: e.target.value })} /></div>
                <div><label className="text-sm text-slate-600 mb-1 block">Last Name</label><Input value={editForm.last_name} onChange={(e) => setEditForm({ ...editForm, last_name: e.target.value })} /></div>
              </div>
              <div><label className="text-sm text-slate-600 mb-1 block">Email</label><Input type="email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} /></div>
              <div><label className="text-sm text-slate-600 mb-1 block">Phone</label><Input type="tel" value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} /></div>
              <div><label className="text-sm text-slate-600 mb-1 block">Street Address</label><Input value={editForm.street} onChange={(e) => setEditForm({ ...editForm, street: e.target.value })} /></div>
              <div className="grid grid-cols-3 gap-2">
                <div><label className="text-sm text-slate-600 mb-1 block">City</label><Input value={editForm.city} onChange={(e) => setEditForm({ ...editForm, city: e.target.value })} /></div>
                <div><label className="text-sm text-slate-600 mb-1 block">State</label><Input value={editForm.state} onChange={(e) => setEditForm({ ...editForm, state: e.target.value })} maxLength={2} /></div>
                <div><label className="text-sm text-slate-600 mb-1 block">Zip</label><Input value={editForm.zip_code} onChange={(e) => setEditForm({ ...editForm, zip_code: e.target.value })} /></div>
              </div>
              <div className="flex gap-2">
                <Button onClick={saveEdit} disabled={savingEdit} className="bg-teal hover:bg-teal/90 flex-1">
                  {savingEdit ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />} Save Changes
                </Button>
                <Button variant="outline" onClick={() => setEditDialog(false)}>Cancel</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DealerLayout>
  );
}
